import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../Providers/Auth";
import { cn } from "../../../../lib/utils";
import { GlobalBranding } from "../../../../Components/Navbar/Navbar";

import { useState } from "react";
import { Copy, Linkedin } from "lucide-react";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcnComponents/ui/dialog";
import mixpanel from "mixpanel-browser";

export const Header = ({
  theme = "dark",
  bg,
  hideName = false,
}: {
  theme?: "dark" | "light";
  bg?: string;
  hideName?: boolean;
}) => {
  const navigate = useNavigate();

  const authState = useAuth();

  return (
    <div
      className={cn(
        `w-full box-border px-[4%] md:px-[40px] py-3 flex items-center justify-between top-0 left-0 border-b border-b-[#262626]`,
        bg
      )}
    >
      <img
        src={
          theme === "light"
            ? GlobalBranding?.logo?.dark
            : GlobalBranding?.logo?.light
        }
        alt=""
        className="w-20 md:w-32 cursor-pointer"
        onClick={() => {
          navigate("/");
        }}
      />

      <div className="flex gap-5">
        {authState?.loggedUser && (
          <section className="flex items-center gap-3 ">
            {!hideName && (
              <span className="font-public text-sm md:text-xl font-medium text-[#FFF]">
                {authState?.loggedUser?.name}
              </span>
            )}
            <img
              src={
                authState?.loggedUser?.profile ||
                "https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/creatorProfileIcon2.png"
              }
              alt=""
              className="rounded-full md:w-10 md:h-10 w-6 h-6"
            />
          </section>
        )}
      </div>
    </div>
  );
};

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  link: string;
  shareText: string;
}

export function LinkedinPostPreviewPopup({
  isOpen,
  onClose,
  link,
  shareText,
}: PopupProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      mixpanel.track("Linkedin post preview: Copy to clipboard: Clicked");
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const publishToLinkedIn = () => {
    try {
      mixpanel.track("Linkedin post preview: Publish to LinkedIn: Clicked");
      // Encode the text for URL compatibility
      const encodedText = encodeURIComponent(shareText?.trim());

      // LinkedIn share URL
      const linkedInShareURL = `https://www.linkedin.com/feed/?linkOrigin=LI_BADGE&shareActive=true&text=${encodedText}`;

      // Open LinkedIn share window
      window.open(linkedInShareURL, "_blank");
    } catch (error) {
      console.error("Error sharing to LinkedIn:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="md:max-w-md p-2 md:p-4 rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-center text-lg md:text-2xl font-bold">
            🎉 Link Generated!
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-center text-xs md:text-sm text-muted-foreground px-4">
            Share this link with your team, friends, or clients to check the
            LinkedIn Preview of your post. They can edit and generate new links
            for new content - each draft will have a new link, so your data is
            safe!
          </p>
          <div className="p-4 bg-gray-100 rounded-lg">
            <div className="flex justify-center items-center gap-2 mb-2 flex-col md:flex-row">
              <input
                className="w-full md:w-auto flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-m line-clamp-1"
                value={link}
                readOnly
              />
              <Button
                size="sm"
                onClick={() => copyToClipboard()}
                className={`bg-red-500 hover:bg-red-600 text-white w-full md:w-auto ${
                  copied ? "opacity-50" : ""
                }`}
                disabled={copied}
              >
                <Copy className="h-4 w-4 mr-2" />
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
            <div className="flex flex-col items-center justify-between mt-2">
              <button
                onClick={publishToLinkedIn}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
              >
                <Linkedin className="h-4 w-4 mr-2" />
                Publish on LinkedIn
              </button>
              <p className="text-xs text-muted-foreground text-center sm:text-start">
                Tip: Bookmark this feature link for next time use.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
